import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { OrderPageData, SchedulerConfigurationData } from '../../api';
import { localizedLabel } from '../../utils/maskUtils';
import { AreaRangesEditor } from './AreaRangesEditor';
import { PageMasksEditor } from './PageMasksEditor';
import { ConfigGuardLevel, configGuards, hasErrors } from './configGuards';

type SchedulerConfigCardProps = {
  config: SchedulerConfigurationData;
  orderPages: OrderPageData[];
  pagesUsedElsewhere: Set<string>;
  languages: string[];
  language: string;
  disabled: boolean;
  onChange: (config: SchedulerConfigurationData) => void;
  onDelete: () => void;
  onValidityChange: (isValid: boolean) => void;
};

export const SchedulerConfigCard = ({
  config,
  orderPages,
  pagesUsedElsewhere,
  languages,
  language,
  disabled,
  onChange,
  onDelete,
  onValidityChange,
}: SchedulerConfigCardProps) => {
  const { t } = useTranslation();

  const guards = configGuards(config, orderPages, language);
  const configHasErrors = hasErrors(guards);
  const isBelowMinimum = guards.some((guard) => guard.key === 'resourceCountTooLow');

  // Ranges and guards both gate saving, so report them together.
  const [areRangesValid, setAreRangesValid] = useState(true);
  useEffect(
    () => onValidityChange(areRangesValid && !configHasErrors),
    [areRangesValid, configHasErrors, onValidityChange],
  );

  const numberField = (field: 'resource_count' | 'persons_per_resource', min: number, error?: boolean) => (
    <TextField
      size="small"
      type="number"
      sx={{ width: 130 }}
      label={t(`sharedResources.schedulerConfigs.${field}`)}
      value={config[field]}
      disabled={disabled}
      error={error}
      inputProps={{ min, dir: 'ltr' }}
      onChange={(event) => onChange({ ...config, [field]: Number(event.target.value) })}
    />
  );

  return (
    <Accordion variant="outlined" disableGutters>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography fontWeight={600}>
          {localizedLabel(config.label, language) || t('sharedResources.schedulerConfigs.untitled')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ marginInlineStart: 1, alignSelf: 'center' }}>
          {t('sharedResources.schedulerConfigs.summary', {
            resources: config.resource_count,
            areas: (config.areas ?? []).length,
            pages: config.allowed_masks.length,
          })}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Grid container flexDirection="column" wrap="nowrap" gap={2}>
          <Grid container alignItems="center" gap={1} wrap="wrap">
            {languages.map((lang) => (
              <TextField
                key={lang}
                size="small"
                sx={{ width: 160 }}
                label={t(`emailTemplates.${lang}`)}
                value={(config.label as Record<string, string | undefined>)?.[lang] ?? ''}
                disabled={disabled}
                onChange={(event) => onChange({ ...config, label: { ...config.label, [lang]: event.target.value } })}
              />
            ))}
            {numberField('resource_count', 1, isBelowMinimum)}
            {numberField('persons_per_resource', 1)}
            <FormControlLabel
              control={
                <Switch
                  checked={config.require_adjacent_resources}
                  disabled={disabled}
                  onChange={(event) => onChange({ ...config, require_adjacent_resources: event.target.checked })}
                />
              }
              label={t('sharedResources.schedulerConfigs.requireAdjacent')}
            />
            <Tooltip title={t('sharedResources.schedulerConfigs.deleteConfig')}>
              <span>
                <Button color="error" size="small" startIcon={<Delete />} disabled={disabled} onClick={onDelete}>
                  {t('sharedResources.schedulerConfigs.deleteConfig')}
                </Button>
              </span>
            </Tooltip>
          </Grid>

          {guards.map((guard) => (
            <Alert
              key={`${guard.key}-${JSON.stringify(guard.params ?? {})}`}
              severity={guard.level === ConfigGuardLevel.ERROR ? 'error' : 'warning'}
              sx={{ paddingBlock: 0 }}>
              {t(`sharedResources.schedulerConfigs.guard_${guard.key}`, guard.params)}
            </Alert>
          ))}

          <Divider />

          <AreaRangesEditor
            areas={config.areas ?? []}
            resourceCount={config.resource_count}
            languages={languages}
            disabled={disabled}
            onChange={(areas) => onChange({ ...config, areas })}
            onValidityChange={setAreRangesValid}
          />

          <Divider />

          <PageMasksEditor
            allowedMasks={config.allowed_masks}
            areas={config.areas ?? []}
            resourceCount={config.resource_count}
            orderPages={orderPages}
            pagesUsedElsewhere={pagesUsedElsewhere}
            languages={languages}
            language={language}
            disabled={disabled}
            onChange={(allowedMasks) => onChange({ ...config, allowed_masks: allowedMasks })}
          />
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
